import { getGDPRConsent, storeGDPRConsent, isGDPRCountry } from "./serviceConsentManager";
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from "./cookieUtils";

class CMPService {
  constructor() {
    this.USP_CONSENT_KEY = "uspConsentState";
    this.GPP_CONSENT_KEY = "gppConsentState";
    this.isInitialized = false;
    this.consentListeners = [];

    this.isGDPR = isGDPRCountry();
    this.showBanner = false;

    this.consentData = {};
    this.uspConsent = "1YNN";
    this.gppConsent = "GPP0";

    this.eventListeners = {};

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.ensureIframe("__tcfapiLocator");
    this.ensureIframe("__uspapiLocator");
    this.ensureIframe("__gppLocator");

    this.loadConsent();

    this.initTCFAPI();
    this.initUSPAPI();
    this.initGPPAPI();

    this.isInitialized = true;
  }

  ensureIframe(name) {
    if (!window.frames[name]) {
      const frame = document.createElement("iframe");
      frame.style.display = "none";
      frame.name = name;
      document.body.appendChild(frame);
    }
  }

  loadConsent() {
    if (this.isInitialized) {
      return;
    }

    if (this.isGDPR) {

      this.consentData = getGDPRConsent() || {};

      // Normalize gdprApplies to boolean (handles 0/1 or "0"/"1")
      this.consentData.gdprApplies = !!this.consentData.gdprApplies;

      if (!this.consentData.tcString) {
        this.showBanner = true;
        //console.warn("[CMP] No GDPR consent found. Showing banner.");
      } else {
        //console.log("[CMP] GDPR Consent loaded:", this.consentData);
      }
    } else {

      this.consentData = {
        gdprApplies: false,
        tcString: "",
        storedPurposeConsents: {},
        storedVendorConsents: {},
        storedLegitimateInterests: {},
        storedSpecialFeatureOptIns: {},
        storedFeatures: {},
        storedVendorLegitimateInterests: {},
      };
    }

    this.uspConsent = getLocalStorageItem(this.USP_CONSENT_KEY, "1YNN");


    const raw = getLocalStorageItem(this.GPP_CONSENT_KEY, { gppConsent: true, gppString: "GPP0" });
     this.gppConsent = raw && typeof raw === 'object' && raw.gppString
       ? raw.gppString
       : "GPP0";

    this.notifyConsentListeners();
    this.propagateConsentToPrebidInstances();

  }

// Helper: push static consent config into a pbjs-like instance safely
applyConsentToPbjsInstance(pbjsInstance, consentConfig) {
  if (!pbjsInstance) return;
  try {
    // If a queue exists, push into it so Prebid processes config in init flow
    if (Array.isArray(pbjsInstance.que)) {
      pbjsInstance.que.push(() => {
        try { pbjsInstance.setConfig && pbjsInstance.setConfig(consentConfig); } catch (e) { /* ignore */ }
      });
      return;
    }

    // If instance is live with setConfig, call it directly
    if (typeof pbjsInstance.setConfig === 'function') {
      try { pbjsInstance.setConfig(consentConfig); } catch (e) { /* ignore */ }
      return;
    }

    // Otherwise create a queue so future prebid build will process the config
    pbjsInstance.que = pbjsInstance.que || [];
    pbjsInstance.que.push(() => {
      try { pbjsInstance.setConfig && pbjsInstance.setConfig(consentConfig); } catch (e) { /* ignore */ }
    });
  } catch (err) {
    console.warn('applyConsentToPbjsInstance error', err);
  }
}

// Build a Prebid-compatible static consent config from CMP state
buildStaticConsentConfig() {
  const cfg = { consentManagement: {}, consentData: {} };

  // GDPR / TCF v2
  if (this.consentData && this.consentData.tcString) {
    cfg.consentData.consentString = this.consentData.tcString;
    cfg.consentData.gdprApplies = !!this.consentData.gdprApplies;
    cfg.consentManagement.gdpr = {
      cmpApi: 'static',      // IMPORTANT: 'static' tells prebid to use provided string and not call CMP
      timeout: 2000,
    };
  }

  // USP / CCPA
  if (this.uspConsent) {
    cfg.consentData.uspString = this.uspConsent;
    cfg.consentManagement.usp = {
      cmpApi: 'static',
      timeout: 2000
    };
  }

  // GPP / other
  if (this.gppConsent) {
    cfg.consentData.gppString = this.gppConsent;
    cfg.consentManagement.gpp = {
      cmpApi: 'static',
      timeout: 2000
    };
  }

  return cfg;
}

// Propagate consent to both runtimes (mypbjs and pbjs)
// Call this after loadConsent() and after setConsent in __tcfapi
propagateConsentToPrebidInstances() {
  const consentConfig = this.buildStaticConsentConfig();

  // If no meaningful consent data, do not propagate
  if (!consentConfig.consentData.consentString && !consentConfig.consentData.uspString && !consentConfig.consentData.gppString) {
    return false;
  }

  // Ensure our own namespace exists (but do NOT stomp third-party pbjs if it exists)
  window.mypbjs = window.mypbjs || { que: [] };

  // Create a benign queue stub for pbjs only if it does not exist.
  // This will allow third-party pbjs (if it loads later) to process the queued setConfig.
  if (!window.pbjs) {
    window.pbjs = { que: [] };
  }

  // Apply config to both
  this.applyConsentToPbjsInstance(window.mypbjs, consentConfig);
  this.applyConsentToPbjsInstance(window.pbjs, consentConfig);

  // helpful debug log
  if (typeof console !== 'undefined') {
    console.info('[CMP] propagated consent to prebid instances', {
      mypbjs: !!window.mypbjs,
      pbjs: !!window.pbjs,
      consentKeys: Object.keys(consentConfig.consentData)
    });
  }

  return true;
}



  subscribeToConsentChanges(callback) {
    this.consentListeners.push(callback);
    return () => {
      this.consentListeners = this.consentListeners.filter(listener => listener !== callback);
    };
  }

  notifyConsentListeners() {
    const normalizedConsent = {
      ...this.consentData,
      gdprApplies: !!this.consentData.gdprApplies
    };

    this.consentListeners.forEach(callback => {
      callback({
        isGDPR: this.isGDPR,
        showBanner: this.showBanner,
        uspConsent: this.uspConsent,
        gppConsent: this.gppConsent,
        consentData: normalizedConsent,
      });
    });
  }



  addEventListener(callback) {
    const listenerId = Object.keys(this.eventListeners).length + 1;
    this.eventListeners[listenerId] = callback;

    return listenerId;
  }

  removeEventListener(listenerId) {
    if (this.eventListeners[listenerId]) {
      delete this.eventListeners[listenerId];
      return true;
    }
    return false;
  }

  initTCFAPI() {
    window.__tcfapi = (cmd, version, callback, parameter) => {
      // create a normalized tcData object to return to callers
      const buildTcData = () => {
        const normalized = { ...this.consentData };
        normalized.gdprApplies = !!normalized.gdprApplies; // ensure boolean
        normalized.eventStatus = "tcloaded";
        normalized.cmpStatus = "loaded";
        return normalized;
      };

      switch (cmd) {
        case "ping":
          {
            const tcData = buildTcData();
            callback({ gdprApplies: tcData.gdprApplies, cmpLoaded: true, cmpStatus: "loaded" }, true);
          }
          break;

        case "getTCData":
          callback(buildTcData(), true);
          break;

        case "addEventListener": {
          const listenerId = this.addEventListener(callback);
          const tcData = buildTcData();
          callback({ ...tcData, listenerId }, true);
          break;
        }

        case "removeEventListener": {
          const success = this.removeEventListener(parameter);
          callback({ success }, success);
          break;
        }

        case "setConsent":
          {
            // Ensure incoming parameter has boolean gdprApplies
            if (parameter && typeof parameter === 'object') {
             parameter.gdprApplies = !!parameter.gdprApplies;
             storeGDPRConsent(parameter);
           } else {
             // nothing to store if parameter invalid; still update internal state defensively
             parameter = parameter || {};
           }

     
            Object.assign(this.consentData, parameter);

            // Build normalized tcData for listener invocations
            const tcData = buildTcData();
            Object.values(this.eventListeners).forEach(listener => listener(tcData, true));

            this.notifyConsentListeners(); // Notify all listeners about consent change

            this.propagateConsentToPrebidInstances();

            callback({ success: true }, true);
          }
          break;

        default:
          callback(null, false);
      }
    };

    window.__cmp = window.__tcfapi;
  }

  initUSPAPI() {
    window.__uspapi = (cmd, version, callback) => {

      if (cmd === "getUSPData" && version === 1) {
        callback({ uspString: this.uspConsent, version: 1 });
      } else if (cmd === "registerDeletion") {
        console.warn("[CMP] User requested data deletion. Removing USP consent...");
        removeLocalStorageItem(this.USP_CONSENT_KEY);
        callback({ success: true }, true);
        this.notifyConsentListeners();
      } else {
        callback(null, false);
      }
    };

  }

  initGPPAPI() {
  const self = this;

  // simple listener store
  self._gppListeners = {};
  self._gppListenerId = 0;

  function currentPing() {
    return {
      gppVersion: '1.1',          // Prebid expects 1.1 per your module
      cmpStatus: 'loaded',
      cmpDisplayStatus: 'hidden',
      signalStatus: 'ready',
      gppString: typeof self.gppConsent === 'string' ? self.gppConsent : 'GPP0',
      applicableSections: self.applicableSections || [],
      parsedSections: self.sections || {}
    };
  }

  function handle(command, cb, parameter) {
    switch (command) {
      case 'ping':
        cb(currentPing(), true);
        break;

      case 'getGPPData': {
        // ensure we return a STRING
        const v = self.gppConsent;
        const gppString =
          typeof v === 'string' ? v :
          (v && typeof v.gppString === 'string') ? v.gppString : 'GPP0';

        cb({
          gppString,
          applicableSections: self.applicableSections || [],
          sections: self.sections || {}
        }, true);
        break;
      }

      case 'addEventListener': {
        const id = ++self._gppListenerId;
        self._gppListeners[id] = cb;
        // immediately notify current state as "ready"
        cb({
          eventName: 'signalStatus',
          listenerId: id,
          pingData: currentPing()
        }, true);
        break;
      }

      case 'removeEventListener': {
        const removed = !!self._gppListeners[parameter];
        delete self._gppListeners[parameter];
        cb(removed, true);
        break;
      }

      case 'registerDeletion':
        removeLocalStorageItem(self.GPP_CONSENT_KEY);
        cb({ success: true }, true);
        self.notifyConsentListeners();
        // optionally notify listeners of change:
        Object.values(self._gppListeners).forEach(fn => fn({
          eventName: 'sectionChange',
          pingData: currentPing()
        }, true));
        break;

      default:
        cb(null, false);
    }
  }

  // accept both signatures: __gpp(cmd, cb[, param]) and __gpp(cmd, ver, cb[, param])
  window.__gpp = function(...args) {
    const command = args[0];
    let cb, parameter;
    if (typeof args[1] === 'function') { cb = args[1]; parameter = args[2]; }
    else if (typeof args[2] === 'function') { cb = args[2]; parameter = args[3]; }
    else { return; }
    handle(command, cb, parameter);
  };

  window.__gppapi = window.__gpp; // optional alias
}






}

const cmpInstance = new CMPService();
export default cmpInstance;
